import React, { useState } from "react";
import { format } from "date-fns";
import { FiChevronDown } from "react-icons/fi";
import { formatCurrency } from "../../utils/formatCurrency";
import {
  isRealInvoiceAmendment,
  pickAmendmentReason,
} from "../../utils/bookingHistory";

const formatPaymentDate = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return String(value);
  }
};

export const paymentStatusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "paid") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "credit") return "bg-sky-100 text-sky-800 border-sky-200";
  if (s === "pending") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "n/a" || s === "na") return "bg-stone-100 text-stone-600 border-stone-200";
  if (s.includes("paid")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s.includes("credit")) return "bg-sky-100 text-sky-800 border-sky-200";
  if (s.includes("pending") || s.includes("due"))
    return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-stone-100 text-stone-700 border-stone-200";
};

const isPaidHistoryRow = (row) => {
  if (!row || typeof row !== "object") return false;
  if (row.isPaid === true) return true;
  return String(row.statusName || "").trim().toLowerCase() === "paid";
};

/** Paid dues have adjustmentType null; never show a Due badge on a paid row. */
const shouldShowAdjustmentBadge = (row) => {
  const adj = String(row?.adjustmentType || "").trim();
  if (!adj) return false;
  if (adj.toLowerCase() === "due" && isPaidHistoryRow(row)) return false;
  return true;
};

/**
 * Shared Payment History panel (customer dashboard + admin parcel history).
 * @param {{ history: object|null, symbol?: string, loading?: boolean, emptyMessage?: string, defaultOpen?: boolean }} props
 */
const PaymentHistoryTimeline = ({
  history,
  symbol = "GBP",
  loading = false,
  emptyMessage,
  defaultOpen = false,
}) => {
  const items = Array.isArray(history?.items) ? history.items : [];
  const overallStatus = String(history?.paymentStatus || "").trim();
  const [open, setOpen] = useState(defaultOpen);

  if (loading) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 text-sm text-stone-600">
        Loading payment history…
      </div>
    );
  }

  if (!items.length) {
    if (!emptyMessage) return null;
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 text-sm text-stone-600">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 min-w-0 overflow-hidden">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5 text-left hover:bg-emerald-50/80"
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
            Payment history
          </p>
          {overallStatus ? (
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${paymentStatusBadgeClass(
                overallStatus,
              )}`}
            >
              {overallStatus}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-stone-600">
          {history.totalPayments != null ? (
            <span className="rounded-md border border-emerald-200 bg-white px-2 py-0.5">
              {history.totalPayments} payment
              {Number(history.totalPayments) === 1 ? "" : "s"}
            </span>
          ) : null}
          {history.paidCount != null ? (
            <span className="rounded-md border border-emerald-200 bg-white px-2 py-0.5 text-emerald-800">
              {history.paidCount} paid
            </span>
          ) : null}
          {history.totalPaidAmount != null &&
          Number(history.totalPaidAmount) > 0 ? (
            <span className="rounded-md border border-emerald-200 bg-white px-2 py-0.5">
              Paid{" "}
              {formatCurrency(
                history.totalPaidAmount,
                history.symbol || symbol,
              )}
            </span>
          ) : null}
          {history.totalPendingAmount != null &&
          Number(history.totalPendingAmount) > 0 ? (
            <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-900">
              Pending{" "}
              {formatCurrency(
                history.totalPendingAmount,
                history.symbol || symbol,
              )}
            </span>
          ) : null}
          <FiChevronDown
            className={`shrink-0 text-emerald-800 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </div>
      </button>

      {open ? (
      <div className="px-3 pb-3 space-y-3">
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-emerald-100 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50/80 text-[10px] uppercase tracking-wide text-emerald-900">
            <tr>
              <th className="px-3 py-2 text-left font-bold">Event</th>
              <th className="px-3 py-2 text-right font-bold">Amount</th>
              <th className="px-3 py-2 text-left font-bold">Method</th>
              <th className="px-3 py-2 text-left font-bold">Status</th>
              <th className="px-3 py-2 text-left font-bold">Paid on</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {items.map((row, idx) => {
              const paid = isPaidHistoryRow(row);
              const pending = row.isPending || !paid;
              return (
                <tr
                  key={`${row.paymentId || row.paymentInvoiceNo || idx}-${row.sequence ?? idx}`}
                  className={pending ? "bg-amber-50/40" : ""}
                >
                  <td className="px-3 py-2.5 align-top">
                    <p className="font-semibold text-stone-900">
                      {row.eventLabel ||
                        row.paymentInvoiceNo ||
                        `Payment ${row.sequence ?? idx + 1}`}
                    </p>
                    {row.paymentInvoiceNo ? (
                      <p className="text-[11px] font-mono text-stone-500 mt-0.5 break-all">
                        {row.paymentInvoiceNo}
                      </p>
                    ) : null}
                    {shouldShowAdjustmentBadge(row) ? (
                      <span className="inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-900">
                        {row.adjustmentType}
                      </span>
                    ) : null}
                    {pickAmendmentReason(row) ? (
                      <p className="text-[11px] text-stone-600 mt-1 break-words">
                        {pickAmendmentReason(row)}
                      </p>
                    ) : null}
                    {isRealInvoiceAmendment(row.adjustmentType) &&
                    row.creationDate ? (
                      <p className="text-[11px] text-stone-500 mt-0.5">
                        Amended {formatPaymentDate(row.creationDate)}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-stone-900 align-top whitespace-nowrap">
                    {row.orderAmount != null
                      ? formatCurrency(
                          row.orderAmount,
                          row.symbol || row.currencySymbol || symbol,
                        )
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-stone-700 align-top">
                    {row.paymentMethodName || "—"}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                        paid
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : pending
                            ? "bg-amber-100 text-amber-900 border-amber-200"
                            : "bg-stone-100 text-stone-700 border-stone-200"
                      }`}
                    >
                      {row.statusName ||
                        (paid ? "Paid" : pending ? "Pending" : "—")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-stone-700 align-top whitespace-nowrap">
                    {row.paymentDate
                      ? formatPaymentDate(row.paymentDate)
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ol className="sm:hidden space-y-2">
        {items.map((row, idx) => {
          const paid = isPaidHistoryRow(row);
          const pending = row.isPending || !paid;
          return (
            <li
              key={`m-${row.paymentId || row.paymentInvoiceNo || idx}-${row.sequence ?? idx}`}
              className={`relative rounded-lg border bg-white p-3 pl-4 ${
                pending ? "border-amber-200" : "border-emerald-100"
              }`}
            >
              <span
                className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                  paid ? "bg-emerald-500" : "bg-amber-400"
                }`}
              />
              <p className="text-sm font-semibold text-stone-900">
                {row.eventLabel ||
                  row.paymentInvoiceNo ||
                  `Payment ${row.sequence ?? idx + 1}`}
              </p>
              {shouldShowAdjustmentBadge(row) ? (
                <span className="inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-900">
                  {row.adjustmentType}
                </span>
              ) : null}
              {pickAmendmentReason(row) ? (
                <p className="text-[11px] text-stone-600 mt-1 break-words">
                  {pickAmendmentReason(row)}
                </p>
              ) : null}
              {isRealInvoiceAmendment(row.adjustmentType) &&
              row.creationDate ? (
                <p className="text-[11px] text-stone-500 mt-0.5">
                  Amended {formatPaymentDate(row.creationDate)}
                </p>
              ) : null}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <span className="text-stone-500 font-semibold uppercase">
                  Amount
                </span>
                <span className="font-semibold text-stone-900 text-right tabular-nums">
                  {row.orderAmount != null
                    ? formatCurrency(
                        row.orderAmount,
                        row.symbol || row.currencySymbol || symbol,
                      )
                    : "—"}
                </span>
                <span className="text-stone-500 font-semibold uppercase">
                  Method
                </span>
                <span className="text-stone-800 text-right">
                  {row.paymentMethodName || "—"}
                </span>
                <span className="text-stone-500 font-semibold uppercase">
                  Status
                </span>
                <span className="text-right font-semibold text-stone-800">
                  {row.statusName ||
                    (paid ? "Paid" : pending ? "Pending" : "—")}
                </span>
                <span className="text-stone-500 font-semibold uppercase">
                  Paid on
                </span>
                <span className="text-stone-800 text-right">
                  {row.paymentDate
                    ? formatPaymentDate(row.paymentDate)
                    : "—"}
                </span>
              </div>
              {row.paymentInvoiceNo ? (
                <p className="mt-2 text-[11px] font-mono text-stone-500 break-all">
                  {row.paymentInvoiceNo}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      </div>
      ) : null}
    </div>
  );
};

export default PaymentHistoryTimeline;
